import { useMemo } from 'react';

import { Badge } from '@/shared/components/shadcn/badge';
import { TooltipFlat } from '@/shared/components/shadcn/tooltip/tooltip-flat';
import { Info } from 'lucide-react';
import type { ExtendedProfile, Profile } from '../../../../entities/post/models';
import type { TableColumnConfig } from '../../../../shared/components/data-table';
import { DataTable } from '../../../../shared/components/data-table';
import { showProfilePopup } from '../../shared/profile-popup/index';
import { getProfileUrl } from '../helpers/get-profile-url';
import { ProfileCell } from './profile-cell/profile-cell';
import { TableHeader } from './table-header';

interface Props {
  profiles: ExtendedProfile[];
  onProfileClick?: (profile: Profile, event: React.MouseEvent) => void;
  isLoading?: boolean;
}

export function ProfileTable({ profiles, onProfileClick, isLoading }: Props) {
  const columns = useMemo(
    (): TableColumnConfig<ExtendedProfile>[] => [
      {
        key: 'username',
        header: 'Profiles',
        size: 'auto',
        tableCellClassName: 'p-0',
        cell: ({ row }) => (
          <ProfileCell
            profileImageUrl={row.original.profileImageUrl}
            username={row.original.username}
            followerCount={row.original.followerCount}
            className="cursor-pointer p-4"
            platform={row.original.platform}
            onClick={async (event) => {
              event.stopPropagation();
              await showProfilePopup({
                position: { x: event.clientX, y: event.clientY },
                profileImageUrl: row.original.profileImageUrl,
                username: row.original.username,
                followerCount: row.original.followerCount,
                profileUrl: getProfileUrl(row.original.username, row.original.platform),
              });
            }}
          />
        ),
      },
      {
        key: 'postCount',
        header: 'Volume',
        size: '124px',
        enableSorting: true,
        cell: ({ getValue }) => <DataTable.Cell getValue={() => getValue()} />,
      },
      {
        key: 'totalViews',
        header: 'Views',
        size: '124px',
        enableSorting: true,
        cell: ({ getValue }) => <DataTable.Cell getValue={() => getValue()} />,
      },
      {
        key: 'totalLikes',
        header: 'Likes',
        size: '124px',
        enableSorting: true,
        cell: ({ getValue }) => <DataTable.Cell getValue={() => getValue()} />,
      },
      {
        key: 'totalShares',
        header: 'Shares',
        size: '124px',
        enableSorting: true,
        cell: ({ getValue }) => <DataTable.Cell getValue={() => getValue()} />,
      },
      {
        key: 'totalComments',
        header: 'Comments',
        size: '124px',
        enableSorting: true,
        cell: ({ getValue }) => <DataTable.Cell getValue={() => getValue()} />,
      },
      {
        key: 'engagementRate',
        header: (
          <TooltipFlat
            asChild
            contentClassName="w-[384px]"
            content={
              <>
                <span className="font-bold">Engagement Rate</span>
                <br />
                [(likes + comments + shares)/ followers count] / Post volume
                <br />
                <br />
                Shows how effectively the creator engages their followers across all posts.
              </>
            }
          >
            <div className="flex items-center gap-1.5">
              <span>ER</span>
              <Info className="h-4 w-4" />
            </div>
          </TooltipFlat>
        ),
        size: '124px',
        enableSorting: true,
        cell: ({ getValue }) => {
          const value = getValue();
          if (typeof value === 'number') {
            const displayValue = value.toFixed(1);
            return <Badge variant="secondary">{displayValue}%</Badge>;
          }
          return <Badge variant="secondary">{value}%</Badge>;
        },
      },
    ],
    [],
  );

  return (
    <DataTable
      data={profiles}
      header={<TableHeader title="Top 100 profiles" />}
      columns={columns}
      onRowClick={onProfileClick}
      pageSize={10}
      minHeight="520px"
      isLoading={isLoading}
    />
  );
}
